import { FC, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Chip,
    LinearProgress,
    Card,
    CardContent,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Breadcrumbs,
    Link as MuiLink,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import { adminApiClient } from '../utils/adminApiClient';
import { useSnackbar } from 'notistack';

interface AppUser {
    id: string;
    tid: string;
    clientId: string | { toString(): string };
    clientApp?: {
        name: string;
    };
    scopes: string[];
    roles?: Array<{
        id: string;
        name: string;
        ident: string;
    }>;
    meta: Record<string, string>;
}

interface Role {
    id: string;
    name: string;
    ident: string;
}

interface MetaEntry {
    key: string;
    value: string;
}

export const AppUserEditPage: FC = () => {
    const { tid, appUserId, clientId } = useParams<{ tid?: string; appUserId: string; clientId?: string }>();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [appUser, setAppUser] = useState<AppUser | null>(null);
    const [clientRoles, setClientRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Form state
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [scopes, setScopes] = useState<string[]>([]);
    const [newScope, setNewScope] = useState('');
    const [metaEntries, setMetaEntries] = useState<MetaEntry[]>([]);
    const [newMetaKey, setNewMetaKey] = useState('');
    const [newMetaValue, setNewMetaValue] = useState('');

    useEffect(() => {
        if (appUserId) {
            loadAppUser();
        }
    }, [tid, appUserId, clientId]);

    const loadAppUser = async () => {
        if (!appUserId) return;
        try {
            setLoading(true);
            // If we have clientId but no tid, we need to get the app user differently
            // For now, we'll need tid from the app user data itself
            let appUserData;
            if (tid) {
                appUserData = await adminApiClient.getAppUser(tid, appUserId);
            } else {
                // If we only have clientId, we need to find the app user from the client's app users
                // This is a workaround - ideally we'd have a direct endpoint
                if (clientId) {
                    const { data: appUsersList } = await adminApiClient.getClientAppUsers(clientId);
                    const foundAppUser = appUsersList.find((au: { id?: string; _id?: string }) => au.id === appUserId || au._id === appUserId);
                    if (!foundAppUser) {
                        throw new Error('AppUser nicht gefunden');
                    }
                    appUserData = foundAppUser;
                } else {
                    throw new Error('tid oder clientId erforderlich');
                }
            }
            
            // Convert clientId to string if it's an ObjectId
            const convertedAppUser = {
                ...appUserData,
                clientId: String(appUserData.clientId)
            };
            setAppUser(convertedAppUser);
            
            // Set form state
            setSelectedRoles(appUserData.roles?.map((r: { ident: string }) => r.ident) || []);
            setScopes(appUserData.scopes || []);
            
            // Convert meta object to array
            const metaArray: MetaEntry[] = Object.entries(appUserData.meta || {}).map(([key, value]) => ({
                key,
                value: String(value)
            }));
            setMetaEntries(metaArray);
            
            // Load client roles
            if (convertedAppUser.clientId) {
                await loadClientRoles(convertedAppUser.clientId);
            }
        } catch (error: any) {
            console.error('Failed to load app user:', error);
            enqueueSnackbar('Fehler beim Laden des App-Benutzers', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const loadClientRoles = async (clientId: string) => {
        try {
            const roles = await adminApiClient.getClientRoles(clientId);
            setClientRoles(roles);
        } catch (error: any) {
            console.error('Failed to load client roles:', error);
            enqueueSnackbar('Fehler beim Laden der Client-Rollen', { variant: 'error' });
        }
    };

    const handleAddScope = () => {
        if (newScope.trim() && !scopes.includes(newScope.trim())) {
            setScopes([...scopes, newScope.trim()]);
            setNewScope('');
        }
    };

    const handleRemoveScope = (scope: string) => {
        setScopes(scopes.filter(s => s !== scope));
    };

    const handleAddMeta = () => {
        if (newMetaKey.trim() && newMetaValue.trim()) {
            if (!metaEntries.some(e => e.key === newMetaKey.trim())) {
                setMetaEntries([...metaEntries, { key: newMetaKey.trim(), value: newMetaValue.trim() }]);
                setNewMetaKey('');
                setNewMetaValue('');
            } else {
                enqueueSnackbar('Metadaten-Schlüssel existiert bereits', { variant: 'warning' });
            }
        }
    };

    const handleRemoveMeta = (key: string) => {
        setMetaEntries(metaEntries.filter(e => e.key !== key));
    };

    const handleUpdateMeta = (key: string, value: string) => {
        setMetaEntries(metaEntries.map(e => e.key === key ? { ...e, value } : e));
    };

    const handleSave = async () => {
        if (!appUserId) return;
        if (!tid && !clientId) return;
        
        try {
            setSaving(true);
            
            // selectedRoles already contains ident values, so we can use them directly
            const roleIdents = selectedRoles;
            
            // Convert meta entries to object
            const metaObject: Record<string, string> = {};
            metaEntries.forEach(entry => {
                metaObject[entry.key] = entry.value;
            });
            
            // Use tid from appUser if not available in params
            const tidToUse = tid || appUser?.tid;
            if (!tidToUse) {
                throw new Error('TID nicht verfügbar');
            }
            
            await adminApiClient.updateAppUser(tidToUse, appUserId, {
                roles: roleIdents,
                scopes: scopes,
                meta: metaObject
            });
            
            enqueueSnackbar('App-Benutzer erfolgreich aktualisiert', { variant: 'success' });
            
            // Navigate back based on context
            if (clientId) {
                navigate(`/app/admin/clients/${clientId}`);
            } else if (tid) {
                navigate(`/app/admin/users/${tid}`);
            } else {
                navigate('/app/admin/clients');
            }
        } catch (error: any) {
            console.error('Failed to update app user:', error);
            enqueueSnackbar('Fehler beim Aktualisieren des App-Benutzers', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
            </Box>
        );
    }

    if (!appUser) {
        return (
            <Box>
                <Typography variant="h6" color="error">
                    App-Benutzer nicht gefunden
                </Typography>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {
                        if (clientId) {
                            navigate(`/app/admin/clients/${clientId}`);
                        } else if (tid) {
                            navigate(`/app/admin/users/${tid}`);
                        } else {
                            navigate('/app/admin/clients');
                        }
                    }}
                    sx={{ mt: 2 }}
                >
                    Zurück
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    {clientId ? (
                        <>
                            <MuiLink
                                component={Link}
                                to="/app/admin/clients"
                                sx={{ textDecoration: 'none' }}
                            >
                                Clients
                            </MuiLink>
                            <MuiLink
                                component={Link}
                                to={`/app/admin/clients/${clientId}`}
                                sx={{ textDecoration: 'none' }}
                            >
                                {appUser.clientApp?.name || 'Client'}
                            </MuiLink>
                            <Typography color="text.primary">
                                App-Benutzer bearbeiten
                            </Typography>
                        </>
                    ) : (
                        <>
                            <MuiLink
                                component={Link}
                                to="/app/admin/users"
                                sx={{ textDecoration: 'none' }}
                            >
                                Benutzer
                            </MuiLink>
                            {tid && (
                                <MuiLink
                                    component={Link}
                                    to={`/app/admin/users/${tid}`}
                                    sx={{ textDecoration: 'none' }}
                                >
                                    Benutzerdetails
                                </MuiLink>
                            )}
                            <Typography color="text.primary">
                                App-Benutzer bearbeiten
                            </Typography>
                        </>
                    )}
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h4" component="h1" gutterBottom>
                            App-Benutzer bearbeiten
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {appUser.clientApp?.name || 'Unbekannter Client'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Speichern...' : 'Speichern'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => setDeleteDialogOpen(true)}
                        >
                            App-Benutzer löschen
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>App-Benutzer löschen</DialogTitle>
                <DialogContent>
                    <Typography>
                        App-Benutzer für „{appUser.clientApp?.name || 'Unbekannt'}“ wirklich löschen?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={async () => {
                            const tidToUse = tid || appUser?.tid;
                            if (!tidToUse || !appUserId) return;
                            try {
                                await adminApiClient.deleteAppUser(tidToUse, appUserId);
                                setDeleteDialogOpen(false);
                                enqueueSnackbar('App-Benutzer erfolgreich gelöscht', { variant: 'success' });
                                if (clientId) {
                                    navigate(`/app/admin/clients/${clientId}`);
                                } else if (tidToUse) {
                                    navigate(`/app/admin/users/${tidToUse}`);
                                } else {
                                    navigate('/app/admin/clients');
                                }
                            } catch (error: unknown) {
                                console.error('Failed to delete app user:', error);
                                enqueueSnackbar('Fehler beim Löschen des App-Benutzers', { variant: 'error' });
                            }
                        }}
                    >
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>

            <Box sx={{ 
                display: 'grid', 
                gap: 3, 
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
            }}>
                {/* Info Card */}
                <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                App-Benutzer Informationen
                            </Typography>
                            <Box sx={{ 
                                display: 'grid', 
                                gap: 2, 
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }
                            }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Client
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {appUser.clientApp?.name || 'Unbekannt'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        App-Benutzer ID
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {appUser.id}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        TID
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {appUser.tid}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Client ID
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {typeof appUser.clientId === 'string' ? appUser.clientId : appUser.clientId.toString()}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Roles Section */}
                <Box>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Rollen
                        </Typography>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel id="roles-label">Rollen auswählen</InputLabel>
                            <Select
                                labelId="roles-label"
                                multiple
                                value={selectedRoles}
                                onChange={(e) => setSelectedRoles(e.target.value as string[])}
                                input={<OutlinedInput label="Rollen auswählen" />}
                                renderValue={(selected: any) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((roleIdent: string) => {
                                            const role = clientRoles.find(r => r.ident === roleIdent);
                                            return (
                                                <Chip
                                                    key={roleIdent}
                                                    label={role?.name || roleIdent}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            >
                                {clientRoles.map((role) => (
                                    <MenuItem key={role.id} value={role.ident}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <Typography variant="body2" fontWeight="medium">
                                                {role.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {role.ident}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {clientRoles.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Keine Rollen für diesen Client verfügbar
                            </Typography>
                        )}
                    </Paper>
                </Box>

                {/* Scopes Section */}
                <Box>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Scopes
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Neuer Scope"
                                value={newScope}
                                onChange={(e) => setNewScope(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleAddScope();
                                    }
                                }}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddScope}
                                disabled={!newScope.trim() || scopes.includes(newScope.trim())}
                            >
                                Hinzufügen
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {scopes.length > 0 ? (
                                scopes.map((scope) => (
                                    <Chip
                                        key={scope}
                                        label={scope}
                                        onDelete={() => handleRemoveScope(scope)}
                                        color="secondary"
                                        variant="filled"
                                    />
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Keine Scopes
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Box>

                {/* Metadata Section */}
                <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                    <Paper variant="subtle" sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Metadaten
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                                size="small"
                                label="Schlüssel"
                                value={newMetaKey}
                                onChange={(e) => setNewMetaKey(e.target.value)}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                size="small"
                                label="Wert"
                                value={newMetaValue}
                                onChange={(e) => setNewMetaValue(e.target.value)}
                                sx={{ flex: 1 }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleAddMeta();
                                    }
                                }}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddMeta}
                                disabled={!newMetaKey.trim() || !newMetaValue.trim()}
                            >
                                Hinzufügen
                            </Button>
                        </Box>
                        {metaEntries.length > 0 ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Schlüssel</TableCell>
                                            <TableCell>Wert</TableCell>
                                            <TableCell align="right">Aktionen</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {metaEntries.map((entry) => (
                                            <TableRow key={entry.key}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {entry.key}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        value={entry.value}
                                                        onChange={(e) => handleUpdateMeta(entry.key, e.target.value)}
                                                        variant="standard"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleRemoveMeta(entry.key)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Keine Metadaten
                            </Typography>
                        )}
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

